import React from "react";
import { Box, CircularProgress, Dialog, DialogContent, DialogTitle, Slide, Typography } from '@mui/material';
import { TransitionProps } from "@mui/material/transitions";
import { Formik } from "formik";
import * as yup from 'yup';
import { AppColor, Fonts } from "../../../types/Constants";
import { useAuthContext } from "../contexts/AuthContext";
import { useAppDispatch } from "../../../utils/hooks";
import { fetchError, showMessage } from "../../../redux/actions";
import ChangePasswordForm from "./ChangePasswordForm";

interface ChangePasswordDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  // eslint-disable-next-line no-undef
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = (prop) => {

  const auth = useAuthContext();
  const dispatch = useAppDispatch();

  const validationSchema = yup.object({
    oldPassword: yup
      .string()
      .required('Please enter an old password.'),
    newPassword: yup
      .string()
      .required('Please enter a new password.')
      .notOneOf([yup.ref('oldPassword'), undefined], 'Your password must be changed. Please enter a new password.'),
    retypeNewPassword: yup
      .string()
      .oneOf([yup.ref('newPassword'), undefined], 'Passwords must match.')
  });

  return (
    <Dialog
      sx={{
        '& .MuiDialog-paper': {
          backgroundColor: "white",
        },
        '& .MuiDialogContent-root': {
          overflowY: 'hidden',
          paddingLeft: 0,
          paddingRight: 0,
          paddingBottom: 0
        }
      }}
      open={prop.isOpen}
      onClose={() => prop.onClose()}
      TransitionComponent={Transition}
    >
      <DialogTitle>
        <Typography sx={{ fontWeight: Fonts.SEMI_BOLD, fontSize: 20 }}>Change Password</Typography>
      </DialogTitle>
      <DialogContent>
        <Box>
          <Formik
            validateOnChange={false}
            validateOnBlur={true}
            initialValues={{
              oldPassword: '',
              newPassword: '',
              retypeNewPassword: '',
            }}
            validationSchema={validationSchema}
            onSubmit={(data, { setSubmitting }) => {
              setSubmitting(true);

              auth.changePassword(data.oldPassword, data.newPassword, (success, error) => {
                setSubmitting(false);
                if (success) {
                  dispatch(showMessage("Password changed successfully!"));
                  prop.onClose();
                } else {
                  dispatch(fetchError(error ?? ""));
                }
              });
            }}>
            {({ isSubmitting }) =>
              <>
                {isSubmitting && <Box sx={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center"
                }}>
                  <CircularProgress size="3rem" sx={{ color: AppColor.baseOrange }} />
                </Box>}
                <ChangePasswordForm onCancel={() => prop.onClose()} />
              </>
            }
          </Formik>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordDialog;
